import { products, WeatherProduct } from '@fe/weather';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import React from 'react';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { AddOutline, TrashOutline } from '../../../../../icons/icons';
import { useDispatch } from 'react-redux';
import { setSetting } from '../../../../state/slices/settings-slice';
import MarkerText from '../../../../components/inputs/MarkerText';
import { useSearchContext } from '../../context/SettingsSearchContext';

const MAX_COUNT = 10;

interface Item {
    product: WeatherProduct | null;
    record: boolean;
}

export default function SettingsWeatherAlwaysLoad() {
    const alwaysLoadWeather = useAppSelector((state) => state.settings.alwaysLoadWeather);
    const [items, setItems] = React.useState<Item[]>(() =>
        alwaysLoadWeather.map(
            (x) =>
                ({
                    product: products[x.id],
                    record: x.record
                }) satisfies Item
        )
    );

    const allProducts = React.useMemo(() => Object.values(products), []);
    const [options, setOptions] = React.useState(allProducts);
    const [filter, setFilter] = React.useState('');
    const dispatch = useDispatch();
    const context = useSearchContext();

    const save = React.useCallback((newItems: Item[]) => {
        dispatch(
            setSetting(
                'alwaysLoadWeather',
                newItems.filter((x) => x.product).map((x) => ({ id: x.product!.id, record: x.record }))
            )
        );
    }, []);

    const handleProductChange = (e: DropDownListChangeEvent, index: number) => {
        const newItems = [...items];
        newItems[index].product = e.value;
        setItems(newItems);
        save(newItems);
    };

    const handleDelete = (index: number) => {
        const newItems = items.toSpliced(index, 1);
        setItems(newItems);
        save(newItems);
    };

    return (
        <div className="px-3 py-2">
            <div className="d-flex gap-2 align-items-center justify-content-between">
                <div><MarkerText highlight={context.term}>{`Recorded weather products (max: ${MAX_COUNT})`}</MarkerText></div>
                <Button
                    svgIcon={AddOutline}
                    themeColor={'primary'}
                    onClick={() => setItems([...items, { product: null, record: false }])}
                    disabled={items.length >= MAX_COUNT}
                />
            </div>

            {items.length > 0 && (
                <div
                    className="pt-2"
                    style={{ display: 'grid', gridTemplateColumns: 'auto max-content', gap: '0.5rem', alignItems: 'stretch' }}>
                    {items.map((item, i) => (
                        <div key={i} style={{ display: 'contents' }}>
                            <DropDownList
                                data={options}
                                textField="name"
                                itemRender={(li, props) =>
                                    React.cloneElement(
                                        li,
                                        li.props,
                                        <>
                                            {props.dataItem.name} [{props.dataItem.vendor}]
                                        </>
                                    )
                                }
                                valueRender={(element, value) =>
                                    React.cloneElement(
                                        element,
                                        element.props,
                                        value ? (
                                            <>
                                                {value.name} [{value.vendor}]
                                            </>
                                        ) : (
                                            <></>
                                        )
                                    )
                                }
                                value={item.product}
                                filterable={true}
                                onFilterChange={(e) => {
                                    const safeQuery =
                                        'escape' in RegExp
                                            ? (RegExp as any).escape(e.filter.value)
                                            : e.filter.value
                                                  .replaceAll('\\', '')
                                                  .replaceAll('/', '')
                                                  .replaceAll('(', '')
                                                  .replaceAll(')', '');
                                    const regexp = new RegExp(safeQuery, 'i');
                                    setOptions(allProducts.filter((x) => regexp.test(x.name)));
                                    setFilter(e.filter.value);
                                }}
                                onBlur={() => {
                                    setFilter('');
                                    setOptions(allProducts);
                                }}
                                filter={filter}
                                onChange={(e) => handleProductChange(e, i)}></DropDownList>

                            <Button svgIcon={TrashOutline} themeColor={'error'} fillMode="outline" onClick={() => handleDelete(i)} />
                        </div>
                    ))}
                </div>
            )}
            {items.length === 0 && <div className="secondary-text pt-2">Click + button to add new products.</div>}
        </div>
    );
}
